/**
 * 69.62 step 1 (ARCHITECTURE §5.31; DECISIONS 272 (3), the owner: "once we have helicopters and gunships we can do some vietnam
 * style "hot rescue" force composition and tactics stuff"; GameConfig.hotRescue): a hot rescue is a play in order — the side's
 * gunship called hot over the threat first (`cover`); once it has circled RESCUE.coverSeconds the wave ship is let in hot to the
 * squad's landing zone (`lift`, 69.18's pickup at the `rescue` priority); the squad pops its smoke as the lift nears, boards at
 * the ramp, and the lift waits RESCUE.groundSeconds, then goes home with whoever is aboard (`done`). Given up (`aborted`): the
 * gunship lost before the lift is in, a known site over the landing zone or the way in, the squad gone (none of it left at the
 * landing zone — dead, or respawned at the base; on the lift's way in too), the pad's ship not free
 * in time; the lift hit to RESCUE.abortHull or lost, or the landing zone overrun on its way in — a lift given up turns for home
 * with whoever is aboard (69.17's rejoin). Pure; the side's knowledge (its picture, the mounts it has seen). Step 3 (§5.31 (2) B):
 * the lift is one of the side's helicopters when it has one left (liftCraft), else its wave ship — its way in from its own spot.
 */
import type { GameState, Rescue, Vec3 } from '../types';
import { PHYS_DT } from '../constants';
import { RESCUE } from '../constants/action';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from './dmath';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { teamPicture } from './picture';
import { callDropship, callPickup, flightRampFoot, rejoinFlight, gunshipsLeft, helisLeft, heliHome, waitingRiders } from './dropship';
import { flightNow } from './shipNow';
import { siteDiscs, inCover, trackCrossesCover } from './skyRoute';
import { applyOrderInput } from './squads';
import { shipBodyOf } from './shipBody';
import { countOf, defaultOn } from './defaultOn';

type Spot = { planetIndex: number; surfaceDir: Vec3 };
export type RescueEvent =
  | { kind: 'rescueCalled'; rescue: Rescue }
  | { kind: 'rescueLift'; rescue: Rescue }
  | { kind: 'rescueSmoke'; rescue: Rescue }
  | { kind: 'rescueDone'; rescue: Rescue }
  | { kind: 'rescueAborted'; rescue: Rescue };

const ticksOf = (s: number) => Math.round(s / PHYS_DT);
const personOf = (state: Pick<GameState, 'config'>) => PERSON_BODY_HEIGHT * personSetOf(state).k;
const arcOn = (state: GameState, pi: number, a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * state.planets[pi].radius;

/** The side's live rescue, or undefined (one at a time a side). */
export const rescueLive = (state: Pick<GameState, 'rescues'>, team: number): Rescue | undefined =>
  (state.rescues ?? []).find(r => r.team === team && (r.phase === 'cover' || r.phase === 'lift'));

/** 69.62 step 3 (§5.31 (2) B, the lean: "the helicopter is the right lift"): a rescue's lift for `team` now — a helicopter while it has one left (GameConfig.helicopters), else its wave ship (undefined). */
export const liftCraft = (state: GameState, team: number): Rescue['craft'] =>
  countOf(state.config, 'helicopters') && helisLeft(state, team) > 0 ? 'heli' : undefined;

/**
 * 69.62 step 4: the threat for a rescue at `at` pointed at `point` (a person's radial call) — the point, or set out along the line
 * from the squad to RESCUE.offsetPeople where nearer (the gunship's reach on the enemies, not the squad: the commander's own rule);
 * null within a person of the squad (no line to set it out on).
 */
export function rescueThreat(state: GameState, at: Spot, point: Vec3): Spot | null {
  const n = vecNormalize(at.surfaceDir), person = personOf(state), r = state.planets[at.planetIndex].radius;
  const d = arcOn(state, at.planetIndex, n, point), off = RESCUE.offsetPeople * person;
  if (d < person) return null;
  if (d >= off) return { planetIndex: at.planetIndex, surfaceDir: vecNormalize(point) };
  const raw = vecSub(vecNormalize(point), n), t = vecNormalize(vecSub(raw, vecScale(n, vecDot(raw, n)))), a = off / r;
  return { planetIndex: at.planetIndex, surfaceDir: vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(t, dsin(a)))) };
}

/** Where the lift (`craft`: a helicopter, else the wave ship) comes in to `at` from: the wave ship's pad, a helicopter's spot out from the base; or null (none on its planet). */
function liftFrom(state: GameState, team: number, at: Spot, craft: Rescue['craft']): Vec3 | null {
  if (craft === 'heli') return heliHome(state, team, at.planetIndex, vecNormalize(at.surfaceDir));
  const pad = state.shipPads?.[team];
  return pad && pad.planetIndex === at.planetIndex ? pad.dir : null;
}

/**
 * Why a rescue at `at` with its gunship over `threat` is too risky for `team` now, or null: an air-defence site it knows of
 * (69.50) covering the landing zone, the gunship's circle, or the straight way in from where the lift (`craft`; absent: the wave ship) sets out
 * (§5.31 (3), the lean: through gun mounts, never under a known site — one site downs a gunship on station 21–24 times in 24).
 */
export function rescueRisk(state: GameState, team: number, at: Spot, threat: Spot, craft?: Rescue['craft']): string | null {
  const sites = siteDiscs(state, team, at.planetIndex);
  if (!sites.length) return null;
  const from = liftFrom(state, team, at, craft);
  if (inCover(at.surfaceDir, sites) || inCover(threat.surfaceDir, sites)) return 'Under their missiles';
  if (from && trackCrossesCover(from, at.surfaceDir, sites)) return 'Under their missiles';
  return null;
}

/**
 * DECISIONS 282 (a) (the owner: "Sometimes it's probably not worth it to risk a rescue, other times maybe there's some benefit to
 * doing so"): what squad `squadId` is worth bringing out from `at` — RESCUE.worth.person for each of it alive on foot there (onFoot),
 * `crew` more for a crashed crew, `carry` more while one of it carries a story item (69.61 step 4).
 */
export function rescueWorth(state: GameState, squadId: number, at: Spot, crew = false): number {
  const sq = state.squads?.find(s => s.id === squadId);
  const ids = sq?.unitIds ?? [];
  const carrying = (state.modeState.objectives ?? []).some(o => o.item?.carrierId !== undefined && ids.includes(o.item.carrierId));
  return onFoot(state, squadId, at) * RESCUE.worth.person + (crew ? RESCUE.worth.crew : 0) + (carrying ? RESCUE.worth.carry : 0);
}

/**
 * 282 (a): what a rescue at `at` costs `team` — the sortie (RESCUE.cost.lift); each gun mount it knows of over the landing zone or
 * across the straight way in from where the lift (`craft`) sets out (it goes through them, hot); each enemy of `foes` past the
 * squad's own number on foot; the side's last gunship spent (with the cover on); the wave ship wanted at home (RESCUE.waitingRiders
 * waiting for it — not with a helicopter the lift).
 */
export function rescueCost(state: GameState, team: number, squadId: number, at: Spot, foes: number, craft?: Rescue['craft']): number {
  const guns = siteDiscs(state, team, at.planetIndex, true), from = liftFrom(state, team, at, craft), c = vecNormalize(at.surfaceDir);
  const mounts = guns.filter(k => inCover(c, [k]) || (!!from && trackCrossesCover(from, c, [k]))).length;
  return RESCUE.cost.lift + mounts * RESCUE.cost.mount + Math.max(0, foes - onFoot(state, squadId, at)) * RESCUE.cost.outnumbered
    + (RESCUE.cover && gunshipsLeft(state, team) === 1 ? RESCUE.cost.lastGunship : 0)
    + (!craft && waitingRiders(state, team).length >= RESCUE.waitingRiders ? RESCUE.cost.waiting : 0);
}

/** The squad told to hold at `at` till `expires` (a task, over its standing order). The same state when refused. */
function holdAt(state: GameState, team: number, squadId: number, at: Spot, expires: number, tick: number, by: Rescue['by'], useWasm?: boolean): GameState {
  const o = applyOrderInput(state, { kind: 'order', team, to: { squad: squadId }, tier: 'task', order: { kind: 'hold', planetIndex: at.planetIndex, surfaceDir: at.surfaceDir }, expires, by, claim: 'lift' }, tick, { useWasm });   // (69.72 step 5, DECISIONS 304: being lifted, rank 2 — the whole rescue)
  return 'refused' in o ? state : o.state;
}

/**
 * A rescue called: refused with the switch off, a rescue of the side's live, no such squad of the side or none of it at `at`, its
 * lift not ready (a helicopter's: none on its planet to fly from; the wave ship's: not home), too risky (rescueRisk), or the
 * gunship's call refused; else the gunship called hot over `threat` (one of the side's spent) and the squad told to hold at its
 * landing zone till the lift can be in. The lift is picked now (liftCraft) and kept: a helicopter is spent when it is let in.
 */
export function callRescue(state: GameState, team: number, squadId: number, at: Spot, threat: Spot, tick: number, by: Rescue['by'] = 'commander', useWasm?: boolean, cause?: Rescue['cause']): { state: GameState; rescue: Rescue } | { refused: string } {
  if (!state.config.hotRescue || !defaultOn(state.config, 'shipPickup')) return { refused: 'Rescues are off' };
  if (rescueLive(state, team)) return { refused: 'A rescue is under way' };
  const sq = state.squads?.find(s => s.id === squadId);
  if (!sq || sq.team !== team) return { refused: `No squad ${squadId} of the side` };
  if (!onFoot(state, squadId, at)) return { refused: 'The squad is gone' };
  const craft = liftCraft(state, team);
  if (craft) { if (!liftFrom(state, team, at, craft)) return { refused: 'No base to fly from' }; }
  else {
    const pad = state.shipPads?.[team];
    if (!pad || pad.planetIndex !== at.planetIndex || (state.dropships?.flights ?? []).some(f => f.fromPad && (f.padOf ?? f.team) === team)) return { refused: 'The ship is away' };
  }
  const risk = rescueRisk(state, team, at, threat, craft);
  if (risk) return { refused: risk };
  // (RESCUE.cover false: the lift alone, let in at once — the measure's other half)
  const g = RESCUE.cover ? callDropship(state, team, threat, tick, { payload: 'gunship', hot: true, by }) : null;
  if (g && 'refused' in g) return g;
  const base = g ? g.state : state, all = base.rescues ?? [];
  const rescue: Rescue = { id: all.reduce((m, r) => Math.max(m, r.id + 1), 0), team, squadId, at: { planetIndex: at.planetIndex, surfaceDir: vecNormalize(at.surfaceDir) }, threat: { planetIndex: threat.planetIndex, surfaceDir: vecNormalize(threat.surfaceDir) }, phase: 'cover', ...(cause ? { cause } : {}), ...(craft ? { craft } : {}), calledTick: tick, gunshipId: g ? g.flight.id : -1, by };
  let next: GameState = { ...base, rescues: [...all, rescue] };
  next = holdAt(next, team, squadId, rescue.at, (g ? g.flight.landTick + ticksOf(RESCUE.coverSeconds) : tick) + ticksOf(RESCUE.liftWaitSeconds), tick, by, useWasm);
  return { state: next, rescue };
}

/** The squad's living on foot (not aboard, not mounted) within RESCUE.squadPeople of `at` — the squad there, to be rescued. */
function onFoot(state: GameState, squadId: number, at: Spot): number {
  const sq = state.squads?.find(s => s.id === squadId), reach = RESCUE.squadPeople * personOf(state);
  return (sq?.unitIds ?? []).filter(id => {
    const p = state.players.find(q => q.id === id);
    return !!p?.alive && !p.boarded && !p.mount && p.planetIndex === at.planetIndex && arcOn(state, at.planetIndex, p.surfaceDir, at.surfaceDir) <= reach;
  }).length;
}

/** An enemy `team` knows of (its picture, seen within the last 10 s) within RESCUE.overrunPeople of `at`? */
function overrun(state: GameState, team: number, at: Spot, tick: number): boolean {
  const reach = RESCUE.overrunPeople * personOf(state), fresh = ticksOf(10);
  return teamPicture(state, team, tick).some(b => b.planetIndex === at.planetIndex && tick - b.tick <= fresh && arcOn(state, at.planetIndex, b.surfaceDir, at.surfaceDir) <= reach);
}

/**
 * The live rescues this tick: each one's next phase, or its end. The same state (and no events) when none moves. `useWasm`: the
 * Match's kernel, for the squad's orders.
 */
export function stepRescues(state: GameState, tick: number, useWasm?: boolean): { state: GameState; events: RescueEvent[] } {
  const all = state.rescues;
  if (!all?.some(r => r.phase === 'cover' || r.phase === 'lift')) return { state, events: [] };
  let next = state;
  const events: RescueEvent[] = [];
  const set = (r: Rescue) => { next = { ...next, rescues: next.rescues!.map(x => (x.id === r.id ? r : x)) }; };
  const end = (r: Rescue, how: 'done' | 'aborted', extra: Partial<Rescue>) => {
    const out: Rescue = { ...r, ...extra, phase: how, endTick: tick };
    set(out);
    events.push({ kind: how === 'done' ? 'rescueDone' : 'rescueAborted', rescue: out });
  };
  // a lift given up: home from where it is, with whoever is aboard (69.17's rejoin, as a pilot's break-off)
  const turnHome = (id: number) => {
    const flights = next.dropships?.flights ?? [], i = flights.findIndex(f => f.id === id);
    const f = flights[i], now = f ? flightNow(next, f, tick) : null;
    if (!f || !now?.flight) return;
    const home: typeof f = { ...rejoinFlight(next, f, tick, { pos: now.flight.pose.pos, vel: now.vel }, f.origin, true), evade: { kind: 'break', tick } };
    next = { ...next, dropships: { ...next.dropships!, flights: flights.map((x, k) => (k === i ? home : x)) } };
  };
  for (const r0 of all) {
    if (r0.phase !== 'cover' && r0.phase !== 'lift') continue;
    const r = next.rescues!.find(x => x.id === r0.id)!;
    const flights = next.dropships?.flights ?? [];
    if (r.phase === 'cover') {
      const g = flights.find(f => f.id === r.gunshipId);
      if (!g && r.gunshipId >= 0) { end(r, 'aborted', { reason: 'The gunship is down' }); continue; }
      if (!onFoot(next, r.squadId, r.at)) { end(r, 'aborted', { reason: 'The squad is gone' }); continue; }
      const risk = rescueRisk(next, r.team, r.at, r.threat, r.craft);
      if (risk) { end(r, 'aborted', { reason: risk }); continue; }
      const letIn = g ? g.landTick + ticksOf(RESCUE.coverSeconds) : r.calledTick;
      if (tick < letIn) continue;   // (the gunship first: on station, firing, before the lift is let in)
      const p = callPickup(next, r.team, r.squadId, r.at, tick, 'rescue', r.craft);
      if ('refused' in p) {
        if (tick - letIn >= ticksOf(RESCUE.liftWaitSeconds) || (g && g.liftTick <= tick)) end(r, 'aborted', { reason: p.refused });
        continue;
      }
      next = holdAt(p.state, r.team, r.squadId, flightRampFoot(p.state, p.flight), p.flight.liftTick, tick, r.by, useWasm);
      const lifted: Rescue = { ...r, phase: 'lift', liftId: p.flight.id, liftTick: tick };
      set(lifted);
      events.push({ kind: 'rescueLift', rescue: lifted });
      continue;
    }
    // lift
    const f = flights.find(x => x.id === r.liftId);
    if (!f) { end(r, 'aborted', { reason: 'The lift is down' }); continue; }
    const aboard = f.pickup?.units ?? [];
    if (tick >= f.liftTick) { end(r, 'done', { brought: [...aboard] }); continue; }   // (lifting with whoever is aboard: the rest is the way home)
    if (f.evade?.kind === 'break') { end(r, 'aborted', { reason: 'The lift broke off', brought: [...aboard] }); continue; }
    const hull = shipBodyOf(f).health;   // (step 3: a helicopter's own, HELI.health)
    if ((f.health ?? hull) <= RESCUE.abortHull * hull) { turnHome(f.id); end(r, 'aborted', { reason: 'The lift is hit', brought: [...aboard] }); continue; }
    if (tick < f.landTick && !aboard.length && !onFoot(next, r.squadId, r.at)) { turnHome(f.id); end(r, 'aborted', { reason: 'The squad is gone', brought: [] }); continue; }   // (none left there to fetch)
    if (tick < f.landTick && overrun(next, r.team, r.at, tick)) { turnHome(f.id); end(r, 'aborted', { reason: 'The landing zone is overrun', brought: [] }); continue; }
    if (r.smokeTick === undefined && tick >= f.landTick - ticksOf(RESCUE.smokeSeconds)) {
      const smoked: Rescue = { ...r, smokeTick: tick };
      set(smoked);
      events.push({ kind: 'rescueSmoke', rescue: smoked });
    }
  }
  return { state: next, events };
}

/** 69.62: the crews who lived through a ship's loss, kept RESCUE.crewSeconds for the commander's call (with the switch on). */
export function noteCrew(state: GameState, team: number, units: readonly number[], tick: number): GameState {
  if (!state.config.hotRescue || !units.length || !state.dropships) return state;
  const keep = (state.dropships.crews ?? []).filter(c => tick - c.tick < ticksOf(RESCUE.crewSeconds));
  return { ...state, dropships: { ...state.dropships, crews: [...keep, { team, units: [...units], tick }] } };
}
